import React, { useState } from 'react';
import {StyleSheet, View, Image, StatusBar} from 'react-native';
import { loadAsync } from 'expo-font';
import { Asset } from "expo-asset";
import { Provider } from "react-redux";
import { NavigationContainer } from "@react-navigation/native";
import { styleVars } from './app-style-vars';
import PtActivityIndicator from "./components/core/pt-activity-indicator/pt-activity-indicator";
import FullScreensNavigation from "./components/navigation/FullScreensNavigation";
import store from "./redux/store";
import getConfig from "./environment";

import { SafeAreaProvider } from "react-native-safe-area-context";
import PtAddPwaToHomeScreenIos from "./components/pt-add-pwa-to-home-screen-ios/pt-add-pwa-to-home-screen-ios";

import UserContext from "./helpers/UserContext";
import LoginScreen from "./screens/LoginScreen";
import Constants from "expo-constants";
import PtUpdateChecker from "./components/pt-update-checker/pt-update-checker";
import AuthService from "./components/core/auth-service/auth-service";
import WrongCountryScreen from "./screens/WrongCountryScreen";
import {getSubdomain} from "./helpers/getSubdomain";
import {getTranslation} from "./helpers/getTranslation";

export default function App() {

  const subdomain = getSubdomain();

  const [assetsLoaded, setAssetsLoaded] = useState(false);

  const gaProfile = getConfig()?.gaScriptProfile;
  const gaEnvironment = getConfig()?.gaScriptEnvironment;

  const cacheImages = (images:any) => {
    return images.map((image:any) => {
      if (typeof image === 'string') {
        return Image.prefetch(image);
      } else {
        return Asset.fromModule(image).downloadAsync();
      }
    });
  };

  const cacheFonts = (fonts:any) => {
    return loadAsync(fonts)
  };

  const cacheTranslations = () => {
    return subdomain !== '' ? require(`./assets/translations/${subdomain}-translations.json`) : require(`./assets/translations/translations.json`);
  };

  const cacheFeatures = () => {
    return require('./assets/countryConfigs.json');
  }

  const getCountryFeatures = (featuresObject: any) => {
    const availableFeatures = [];
    Object.keys(featuresObject).map((featureKey) => {
      if(featuresObject[featureKey][userData.country.toLowerCase()]) {
        availableFeatures.push(featureKey);
      }
      updateUserData({...userData, features: availableFeatures});
    })
  }

  const loadAssets = () => {
    const imageAssets = cacheImages([
      require('./assets/images/supergraphic.png'),
      require('./assets/images/BOSCH_RGB.png'),
      require('./assets/images/ios-share-icon.png'),
    ]);
    const fontAssets = cacheFonts({
      'BoschSansRegular': require('./assets/fonts/BoschSans-Regular.ttf'),
      'BoschSansLight': require('./assets/fonts/BoschSans-Light.ttf'),
      'BoschIcons': require('./assets/fonts/Bosch-Icon.ttf')
    });
    const loadTranslations = cacheTranslations();
    const loadFeaturesConfig = cacheFeatures();

    Promise.all([imageAssets, fontAssets, loadTranslations, loadFeaturesConfig]).then(() => {
      setAssetsLoaded(true);
      getCountryFeatures(loadFeaturesConfig);
    }).catch((err) => {
      alert('Assets not loaded, something broke');
      console.log('Assets not loaded arror = ', err);
    });
  };

  /** google analytics tag script */
  const gascript = (a,b,c,d) => { a=`//tags.tiqcdn.com/utag/robert-bosch/${gaProfile}/${gaEnvironment}/utag.js`;
    b=document;c='script';d=b.createElement(c);d.src=a;d.type='text/java'+c;d.async=true;
    a=b.getElementsByTagName(c)[0];
    a.parentNode.insertBefore(d,a);
  }

  const [gaScriptLoaded, setGaScriptLoaded] = useState(false);

  const [userData, setUserData] = useState({
    authenticated: false,
    availableCountries: [],
    country: subdomain,
    availableSiteUIDs: [],
    baseSiteUID: subdomain,
    language: 'en',
    dataCollected: false,
    features: [],
    impersonatingName: null,
    impersonatingId: null,
    impersonatingUid: null,
    canImpersonate: false,
    mustImpersonate: false,
    salesAreaDependantCustomerUid: null,
    orderRestricted: false,
    orderScenarios: [],
    resetTxtUpload: false,
    resetXlsUpload: false,
    resetCustomerUpload: false
  });

  const updateUserData = (data:any) => {
    console.log('--- inside updateUserData, dataReceived = ', data);
    setUserData({
      ...userData,
      authenticated: data.authenticated || false,
      availableCountries: data.availableCountries || [],
      country: data.country.toLowerCase() || subdomain,
      availableSiteUIDs: data.availableSiteUIDs || [],
      baseSiteUID: data.baseSiteUID || subdomain,
      language: data.language || 'en',
      dataCollected: data.dataCollected || false,
      features: data.features || [],
      impersonatingName: data.impersonatingName,
      impersonatingId: data.impersonatingId,
      impersonatingUid: data.impersonatingUid,
      canImpersonate: data.canImpersonate || false,
      mustImpersonate: data.mustImpersonate || false,
      salesAreaDependantCustomerUid: data.salesAreaDependantCustomerUid,
      orderRestricted: data.orderRestricted || false,
      orderScenarios: data.orderScenarios || [],
      resetTxtUpload: data.resetTxtUpload || false,
      resetXlsUpload: data.resetXlsUpload || false,
      resetCustomerUpload: data.resetCustomerUpload || false
    })
  }

  /** internal application version from local storage */
  const appVersionFromLocalStorage = global.localStorage.getItem('PT_APP_VERSION');
  /** internal application version from app.json */
  const appVersionFromConfig = Constants.manifest.version;
  if(appVersionFromConfig && appVersionFromConfig !== appVersionFromLocalStorage) {
    console.log('updating app version in local storage to ', appVersionFromConfig);
    global.localStorage.setItem('PT_APP_VERSION', appVersionFromConfig);
  }

  if (assetsLoaded) {
    /** prevent multiple loads of gascript on userDataStateChange */
    if(!gaScriptLoaded) {
      gascript();
      setGaScriptLoaded(true);
    }
    return (
      <UserContext.Provider value={{userData, updateUserData}}>
        <Provider store={store}>
          <SafeAreaProvider>
            <View style={styles.statusBarContainer}>
              <StatusBar translucent barStyle={'dark-content'} backgroundColor={'#fff'}/>
            </View>
            <AuthService manual/>
            {console.log("****** ****** ******* \nin app, userData in render = ", userData)}
            {userData.authenticated && userData.dataCollected ?
              (userData.baseSiteUID.indexOf(subdomain) < 0) ?
                <WrongCountryScreen/>
                :
                <NavigationContainer>
                  <FullScreensNavigation/>
                </NavigationContainer>
              :
              <LoginScreen/>
            }
            <PtUpdateChecker/>
            <PtAddPwaToHomeScreenIos/>
          </SafeAreaProvider>
        </Provider>
      </UserContext.Provider>
    )
  } else {
    loadAssets();
    return (
      <View style={styles.mainContainer}>
        <PtActivityIndicator show={true} message={"Loading assets"} />
      </View>
    )
  }
}

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    backgroundColor: styleVars.boschLightGrayW75,
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
  },
  statusBarContainer: {
    height: styleVars.statusBarHeight(),
    backgroundColor: '#fff',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 9999
  },
  serviceWorkerUpdateButtonContainer: {
    paddingHorizontal: styleVars.rem(1),
    paddingVertical: styleVars.rem(.5),
    marginVertical: styleVars.rem(.5),
    marginHorizontal: styleVars.rem(.5),
    backgroundColor: styleVars.boschDarkBlue,
    justifyContent: 'center',
    alignItems: 'center'
  },
  serviceWorkerUpdateButtonText: {
    fontSize: styleVars.rem(.875),
    color: '#fff'
  },
});
